import PropTypes from "prop-types";
import React from "react";
import { displayNumbers, numberToWordsFrench } from "../../../variables/constants";
import { libellePrejudice } from "../../../utils/typePrejudice";
import axiosInstance from "../../../variables/api/axiosInstance";
import logoLocal from "../../../assets/images/logo.png";
import dayjs from "dayjs";
import BlocSignature from '../../../components/BlocSignature';
import EnteteDocumentBncb from './EnteteDocumentBncb';
import PiedDocumentBncb from './PiedDocumentBncb';
import {
    numeroSinistreDuDocument,
    dateSurvenanceDuDocument,
    bureauDuDocument,
    victimeDuDocument,
} from './ordreDepenseReferences';
/* Les tableaux de ce document reprennent ceux de la quittance : meme feuille,
   pour que les deux pieces du meme dossier ne divergent pas. Le miroir
   `quittanceIndemniteRaw.js` sert a l'iframe d'impression. */
import '../../../assets/styles/quittanceIndemnite.css';

const VERT = "#1a6b3c";
const GRIS = "#6b7280";

const resolveBrandingUrl = (path) => {
    if (!path) return null;
    if (/^(https?:|data:)/i.test(path)) return path;
    const base = (axiosInstance.defaults.baseURL || "").replace(/\/$/, "");
    return `${base}${path}`;
};

/**
 * Date en JJ/MM/AAAA, sans heure.
 *
 * `frenchDate` du référentiel colle « - » et l'heure derrière la date (« 14/09/2026 - »
 * sur une date sans heure), et lève sur autre chose qu'une chaîne : l'ordre de dépense
 * d'un règlement sans `createdAt` ne s'imprimait pas du tout.
 */
const jour = (valeur) => {
    if (!valeur) return "";
    // dayjs lit « 2026-09-07 » en heure locale ; new Date() le lit en UTC et la date
    // imprimée reculait d'un jour à l'ouest de Greenwich.
    const d = dayjs(valeur);
    return d.isValid() ? d.format("DD/MM/YYYY") : String(valeur);
};

/** Une ligne « libellé / valeur » du bloc de références, au format de la quittance. */
const Reference = ({ libelle, valeur }) => (
    <tr>
        <td className="quittance-ref-label">{libelle}</td>
        <td className="quittance-ref-value">{valeur || "-"}</td>
    </tr>
);

/**
 * Ordre de dépense - le document qui autorise le paiement.
 *
 * Mise en page reprise le 2026-09-16 : en-tête du bureau (et non de l'assureur), titre et
 * références sur une grille lisible, montant mis en avant, signatures avec leur trait.
 */
const OrdreDeDepenseV2 = React.forwardRef((props, ref) => {

    const reglement = props?.reglement;
    /* Chargee AVANT le rendu : ce document est serialise par renderToStaticMarkup,
       ou aucun effet React ne s'execute et ou BlocSignature ne chargerait rien. */
    const signature = props?.signature || null;
    const sinistre = props?.sinistre;
    const organisme = props?.organisme || sinistre?.contrat?.organisme || {};
    // En-tête, pied de page et signataires : le bureau Carte Brune, pas l'assureur.
    const bureau = bureauDuDocument({ bureau: props?.bureau, organisme, sinistre });
    const lotIndice = props?.lotIndice;

    const beneficiaire =
        reglement?.beneficiaireNom ||
        reglement?.etablissementNom ||
        reglement?.beneficiaire ||
        "";
    const montant = reglement?.montant || 0;

    const banque = reglement?.banque || reglement?.banqueCheque || null;
    const numeroCheque =
        reglement?.numeroCheque || reglement?.numeroChequeEmis || null;
    const dateCheque = reglement?.dateEmissionCheque || reglement?.dateCheque || null;
    const dateSurvenance = dateSurvenanceDuDocument(reglement, sinistre);

    // URL backend relative (`/v1/...`) - préfixe baseURL sinon résolu contre l'origine du frontend.
    const headerImage = resolveBrandingUrl(bureau?.headerImageUrl || bureau?.logo);

    const villeImpression = bureau?.ville || "Lomé";

    const signataire1Nom = bureau?.responsable || "";
    const signataire1Titre = bureau?.titreResponsable || "Directeur Général";
    const afficherSignataire2 = bureau?.afficherDeuxSignatures === true;
    const signataire2Nom = bureau?.responsable2 || "";
    const signataire2Titre = bureau?.titreResponsable2 || "";

    const prejudiceLabel = libellePrejudice(reglement?.typePrejudice);
    const representantText = [
        prejudiceLabel,
        reglement?.motifComplement,
        reglement?.motifReglement,
    ].filter(Boolean).join(" - ");

    const sinistreNumero = numeroSinistreDuDocument(reglement, sinistre);
    /* LA VICTIME, EN REFERENCE ET NON EN BENEFICIAIRE. Sur un ordre de depense
       d'honoraires, c'est l'expert qu'on paie : « a l'ordre de » reste a son nom. Mais la
       piece doit dire pour quelle victime l'expertise a ete faite - sans quoi rien, sur
       le document, ne rattache la depense a un dossier precis. */
    const victime = victimeDuDocument(reglement);

    return (
        <div ref={ref} style={{
            width: "210mm",
            minHeight: "297mm",
            margin: "0 auto",
            backgroundColor: "white",
            padding: "16mm 18mm",
            fontFamily: "Arial, sans-serif",
            fontSize: 13,
            color: "#111",
            position: "relative",
            boxSizing: "border-box",
        }}>

            {/* Meme en-tete que la quittance d'indemnite, repris de l'offre CIMA. */}
            <EnteteDocumentBncb
                bureau={bureau}
                logo={headerImage || logoLocal}
                mentionDroite={lotIndice
                    ? (typeof lotIndice === "string" ? lotIndice
                        : `Lot ${lotIndice.numeroLot} - Ligne ${lotIndice.ligne}/${lotIndice.totalLignes}`)
                    : null}
            />

            {/*  Titre  */}
            <h1 style={{
                textAlign: "center", fontSize: 19, letterSpacing: 2, color: VERT,
                margin: "22px 0 4px", textTransform: "uppercase",
            }}>
                Ordre de dépense
            </h1>
            {reglement?.numeroPaiement && (
                <div style={{ textAlign: "center", fontSize: 12, color: GRIS, marginBottom: 18 }}>
                    N° d'opération : <strong style={{ color: "#111" }}>{reglement.numeroPaiement}</strong>
                </div>
            )}

            {/*  Références du dossier, et le montant qui autorise la dépense  */}
            <div style={{ display: "flex", gap: 16, alignItems: "stretch", marginBottom: 26 }}>
                <table className="quittance-ref-table" style={{ flex: 1 }}>
                    <tbody>
                        <Reference libelle="Sinistre n°" valeur={sinistreNumero} />
                        <Reference libelle="Survenu le" valeur={jour(dateSurvenance)} />
                        {/* Omise, et non affichee avec un tiret : un paiement sans victime
                            rattachee est normal - une indemnite a un organisme, par
                            exemple - et une ligne vide laisserait croire a un oubli. */}
                        {victime && <Reference libelle="Victime" valeur={victime} />}
                        <Reference libelle="Bénéficiaire" valeur={beneficiaire} />
                    </tbody>
                </table>
                <div className="quittance-encadre quittance-encadre--total">
                    <div className="quittance-encadre-titre">Montant en FCFA</div>
                    <div className="quittance-encadre-valeur quittance-encadre-montant">
                        <span className="quittance-montant">{displayNumbers(Math.trunc(montant))}</span>
                    </div>
                </div>
            </div>

            {/*  Corps  */}
            <div style={{ fontSize: 13.5, lineHeight: 1.9, marginBottom: 30 }}>
                <p style={{ margin: "0 0 10px" }}>
                    Veuillez payer à l&apos;ordre de <strong>{beneficiaire || "-"}</strong>,
                </p>
                <p style={{ margin: "0 0 10px" }}>
                    la somme de <strong className="quittance-montant">{numberToWordsFrench(montant)}</strong>.
                </p>
                {representantText && (
                    <p style={{ margin: "0 0 10px", color: "#374151" }}>
                        Représentant : {representantText}.
                    </p>
                )}
                {(banque || numeroCheque || dateCheque) && (
                    <p style={{ margin: "0 0 10px", color: "#374151" }}>
                        {banque && <>Banque : <strong>{banque}</strong></>}
                        {numeroCheque && <> - chèque n° <strong>{numeroCheque}</strong></>}
                        {dateCheque && <> du <strong>{jour(dateCheque)}</strong></>}
                    </p>
                )}
            </div>

            {/*  Lieu et date  */}
            <div style={{ textAlign: "right", fontSize: 13, marginBottom: 34 }}>
                Fait à {villeImpression}, le {jour(reglement?.createdAt || reglement?.dateEmission || new Date())}
            </div>

            {/*  Signatures : un trait, puis le nom et la fonction  */}
            <div style={{ display: "flex", gap: 40, justifyContent: "space-between" }}>
                {[
                    { nom: signataire1Nom, titre: signataire1Titre },
                    ...(afficherSignataire2 ? [{ nom: signataire2Nom, titre: signataire2Titre }] : [{ nom: "", titre: "Comptable" }]),
                ].map((s, i) => (
                    <div key={i} style={{ flex: 1, textAlign: "center" }}>
                        {/* Dans le vide déjà prévu au-dessus du trait : la signature
                            remplace l'espace, elle ne l'allonge pas. Seul le premier
                            signataire est le responsable paramétré ; le second signe
                            de sa main. */}
                        <div style={{
                            height: 54, borderBottom: `1px solid #9ca3af`, marginBottom: 6,
                            display: "flex", alignItems: "flex-end", justifyContent: "center"
                        }}>
                            {i === 0 && (
                                <BlocSignature
                                    codeDocument="ORDRE_DEPENSE"
                                    sinistreTrackingId={sinistre?.sinistreTrackingId}
                                    hauteurMm={13}
                                    alignement="center"
                                    signature={signature}
                                />
                            )}
                        </div>
                        <div style={{ fontWeight: 600 }}>{s.nom}</div>
                        <div style={{ fontSize: 12, color: GRIS }}>{s.titre}</div>
                    </div>
                ))}
            </div>

            {/* Coordonnees du Bureau, comme au bas de l'offre CIMA. */}
            <PiedDocumentBncb bureau={bureau} />
        </div>
    );
});

OrdreDeDepenseV2.propTypes = {
    bureau: PropTypes.object,
    reglement: PropTypes.object,
    sinistre: PropTypes.object,
    organisme: PropTypes.object,
    lotIndice: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            numeroLot: PropTypes.string,
            ligne: PropTypes.number,
            totalLignes: PropTypes.number,
        }),
    ]),
};

OrdreDeDepenseV2.displayName = "OrdreDeDepenseV2";

export default OrdreDeDepenseV2;
